import * as React from 'react';
import { useTelemetry } from '@snowflake/core-ui';
import {
  ABExperimentName,
  countriesKeyedByIsoCode,
  FeatureFlag,
  OidcProvider,
  OidcSignupMechanicalParams,
  SignupEventType,
  SignupFlowType,
} from '@signup/shared';
import { OidcStartError, SignupApi } from '../../api/signupApi';
import { OIDC_FORM_ID_STORAGE_KEY, SignupEventName } from '../../utils/SignupLogger';
import {
  getExperimentGroup,
  getSignupFlowTypeWithoutListener,
  isFeatureEnabled,
} from '../../utils/configurations';
import { AWS_AP_SOUTH_1_REGION, isIPFromIndia } from '../../utils/RegionSelect';
import { useSignupFormDataOptional } from '../Cards/forms/SignupCardV2/SignupFormDataContext';
import { parseQueryParams } from '../../utils/ParseQueryParams';
import { getOidcUnsupportedReasons } from '../../utils/oidcSupport';

interface SocialLoginButtonsProps {
  formId: string;
}

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.001 24.001 0 0 0 0 21.56l7.98-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
    <path fill="none" d="M0 0h48v48H0z" />
  </svg>
);

const MicrosoftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

export const OIDC_SIGNUP_CONTEXT_KEY = 'oidcSignupContext';

// Server error codes from start-oidc-signup meaning "this signup link cannot use OIDC". Shown as
// an explanatory inline message pointing at the email form rather than a retry prompt, since
// retrying can never succeed.
const OIDC_START_UNSUPPORTED_CODES: readonly string[] = ['OIDC_FLOW_TYPE_UNSUPPORTED'];

// query-string parses a valueless param (`?utm_source`) to null, which would survive JSON and
// sessionStorage and fail the server's typeof check. Only real values are carried forward.
export const stringOrUndefined = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;
const booleanOrUndefined = (value: unknown): boolean | undefined =>
  typeof value === 'boolean' ? value : undefined;

export interface OidcSignupContext extends OidcSignupMechanicalParams {
  edition: string;
  cloud: string;
  region: string;
  country: string;
  // Captured here (on the signup page) so it survives the IdP redirect; the /oidc/callback page
  // cannot re-derive it from its own URL. Sent on the complete request to gate provisioning
  // (e.g. the CortexCodeCLI / CoCo path). Optional so sessions started before this field are handled.
  signupFlowType?: SignupFlowType;
  // pathname + search of the signup page at click time (e.g. `/?lab=abc&utm_source=x`), so the
  // callback page's error card can send the user back to the same link instead of a bare `/`.
  // Optional so sessions started before this field are handled. sessionStorage is user-editable,
  // so the callback page validates it as a same-origin relative path on read.
  originalSearch?: string;
}

export const SocialLoginButtons: React.FC<SocialLoginButtonsProps> = ({ formId }) => {
  const { logEvent } = useTelemetry();
  const [pendingProvider, setPendingProvider] = React.useState<OidcProvider | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  // Google and Microsoft are independent experiments -- a session can land in either, both, or
  // neither. Server-side start() enforces the same per-provider gate, so this is the UI half of
  // a real gate, not a cosmetic hide.
  const googleExperimentGroup = React.useMemo(
    () => getExperimentGroup(ABExperimentName.OIDC_SIGNUP_GOOGLE),
    [],
  );
  const microsoftExperimentGroup = React.useMemo(
    () => getExperimentGroup(ABExperimentName.OIDC_SIGNUP_MICROSOFT),
    [],
  );

  // Read once at mount, same as the experiment groups above -- the hide decision below has to
  // happen before any redirect, so it only ever needs the URL as it was when this page loaded.
  const [signupParams, marketingParams] = React.useMemo(
    () => parseQueryParams(window.location.search),
    [],
  );

  // Same read-once-at-mount rule as signupParams above. Checks the raw query string (not the parsed
  // signupParams) because the API derives the flow type from key presence, so a valueless `?eng`
  // still counts.
  const unsupportedReasons = React.useMemo(
    () => getOidcUnsupportedReasons(window.location.search, getSignupFlowTypeWithoutListener()),
    [],
  );

  const formData = useSignupFormDataOptional();

  // Resolve country display name (e.g. "United States") from ISO code (e.g. "US").
  // Server expects the display name; SFDC uses the same.
  const country = formData?.countryIsoCode
    ? (countriesKeyedByIsoCode[formData.countryIsoCode]?.value ?? '')
    : '';

  const hasRequiredContext = Boolean(
    formData?.edition && formData?.cloud && formData?.region && country,
  );

  // Mirrors suppressionService.checkIpAndEmailForIndia on the backend for the OIDC path: the
  // controller never passes awsType/eng/listing into runFullSuppressionChecks for OIDC signups
  // (oidcSignupController.ts), so those exemptions are always undefined there regardless of the
  // query params on this page -- the backend rule for OIDC really is just India IP + Mumbai
  // region. Exempting on them here would let an exempt-looking OIDC user authenticate and still
  // get hard-rejected by AWS_MUMBAI_BLOCK, which is the dead-end this flag exists to avoid.
  //
  // The freemail half of the backend condition is dropped too, but not because it's always true:
  // the client can't know the authenticated email before the IdP redirect, so this intentionally
  // over-hides relative to the backend rule -- it also hides SSO from Google Workspace / Microsoft
  // Entra corporate accounts in Mumbai that would have passed the backend's freemail check. That's
  // an accepted cost of gating client-side before the identity is known, not an oversight.
  const isMumbaiSsoHideApplicable =
    isFeatureEnabled(FeatureFlag.FF_HIDE_SSO_FOR_AWS_MUMBAI_BLOCK) &&
    isIPFromIndia() &&
    formData?.region === AWS_AP_SOUTH_1_REGION;

  // A signup-link token (`t`) means this session needs a guaranteed brand-new account (e.g. a
  // hands-on-lab attendee who needs the lab's trial config). OIDC's existing-account picker can
  // instead route the user into an identity's existing Snowflake account, which both defeats the
  // link's purpose and generates support cases (existing accounts may lack AI features or have
  // exhausted limits) -- the regular email/password flow has no such reuse concept, so it isn't
  // affected. Hiding here avoids the picker entirely rather than trying to special-case it.
  // Unconditional (no feature flag) -- this is an already-decided product behavior, not a staged
  // rollout. See SNOW-4306083.
  // A `program` param (e.g. SCC CoCo promo) has the same need, and the OIDC flow does not carry it
  // through, so an SSO signup would also skip the program approval check and claim.
  // A sales-assisted `eng` param is also not carried through, so an SSO signup would lose the SE
  // validation and the SalesAssisted entitlement and be provisioned as a capped standard trial.
  const isSpecialLinkHideApplicable =
    !!signupParams.t || !!signupParams.program || !!signupParams.eng;

  // Fail closed until SNOW-4313138: if the URL carries a param the regular flow acts on but OIDC
  // does not (eng, listing, ampt, ...), or the signup flow type is one OIDC does not support, hide
  // the buttons rather than let the user sign in and silently get a degraded regular signup. Closed
  // over the known params and flow types in oidcSupport.ts, so tracking params (utm_*, gclid, _ga,
  // ...) never trigger it. Unconditional (no feature flag), like the special-link hide above.
  const isUnsupportedHideApplicable = unsupportedReasons.length > 0;

  // Whether either provider would have rendered absent any hide below -- lets the telemetry
  // below distinguish "hidden" sessions (would have shown OIDC) from sessions that were never
  // going to see OIDC buttons in the first place (e.g. DEFAULT_GROUP via an explicit override --
  // both experiments are at full rollout, so this path is no longer reachable organically).
  const wouldShowOidcButtons =
    googleExperimentGroup === 'GOOGLE_OIDC_ENABLED' ||
    microsoftExperimentGroup === 'MICROSOFT_OIDC_ENABLED';

  const isGoogleEnabled =
    googleExperimentGroup === 'GOOGLE_OIDC_ENABLED' &&
    !isMumbaiSsoHideApplicable &&
    !isSpecialLinkHideApplicable &&
    !isUnsupportedHideApplicable;
  const isMicrosoftEnabled =
    microsoftExperimentGroup === 'MICROSOFT_OIDC_ENABLED' &&
    !isMumbaiSsoHideApplicable &&
    !isSpecialLinkHideApplicable &&
    !isUnsupportedHideApplicable;

  // isMumbaiSsoHideApplicable depends on formData?.region, which changes as the user toggles the
  // region dropdown -- without latching, toggling the region back and forth would re-fire these
  // impression events on every change. Each is latched independently (not just once overall)
  // because a session can legitimately fire more than one: it starts on a non-Mumbai region by
  // default, so OIDC_BUTTONS_SHOWN can fire before the user ever picks Mumbai; a special-link
  // token, by contrast, is fixed from the URL at mount, so that hide can only ever fire once. The
  // unsupported-params hide is fixed at mount too, but it sits behind the Mumbai and special-link
  // branches in the chain below, so it still needs its own latch: it is reached again whenever an
  // earlier branch's latch is already set and the effect re-runs.
  const hasLoggedButtonsShown = React.useRef(false);
  const hasLoggedButtonsHidden = React.useRef(false);
  const hasLoggedButtonsHiddenSpecialLink = React.useRef(false);
  const hasLoggedButtonsHiddenUnsupported = React.useRef(false);

  React.useEffect(() => {
    if ((isGoogleEnabled || isMicrosoftEnabled) && !hasLoggedButtonsShown.current) {
      logEvent({
        event: SignupEventName.OIDC_BUTTONS_SHOWN,
        type: SignupEventType.UI_RESPONSE_SUCCESS,
        data: {
          formId,
          googleExperimentGroup,
          microsoftExperimentGroup,
        },
      });
      hasLoggedButtonsShown.current = true;
    } else if (
      wouldShowOidcButtons &&
      isMumbaiSsoHideApplicable &&
      !hasLoggedButtonsHidden.current
    ) {
      // Measures hide volume (and, cross-referenced with the eventual conversion funnel, the
      // Workspace/Entra over-block size) during rollout -- see the over-hide tradeoff noted above.
      logEvent({
        event: SignupEventName.OIDC_BUTTONS_HIDDEN_MUMBAI,
        type: SignupEventType.UI_RESPONSE_SUCCESS,
        data: {
          formId,
          googleExperimentGroup,
          microsoftExperimentGroup,
        },
      });
      hasLoggedButtonsHidden.current = true;
    } else if (
      wouldShowOidcButtons &&
      isSpecialLinkHideApplicable &&
      !hasLoggedButtonsHiddenSpecialLink.current
    ) {
      // Measures hide volume for the special-link case specifically, separate from the Mumbai
      // hide above -- within a single render where both conditions hold, only the Mumbai event
      // fires (branch order). Across a session, though, both can fire: e.g. a session that starts
      // on a non-Mumbai region logs this event first, then logs the Mumbai event once the user
      // picks Mumbai. Only a session that's on Mumbai for its entire lifetime misses this event
      // entirely -- an accepted, low-probability edge case rather than something worth the extra
      // bookkeeping of independent `if`s.
      logEvent({
        event: SignupEventName.OIDC_BUTTONS_HIDDEN_SPECIAL_LINK,
        type: SignupEventType.UI_RESPONSE_SUCCESS,
        data: {
          formId,
          googleExperimentGroup,
          microsoftExperimentGroup,
        },
      });
      hasLoggedButtonsHiddenSpecialLink.current = true;
    } else if (
      wouldShowOidcButtons &&
      isUnsupportedHideApplicable &&
      // A special-link session already has its own event; without this guard it would also log
      // here once the special-link latch is set and the effect re-runs (e.g. a region toggle).
      !isSpecialLinkHideApplicable &&
      !hasLoggedButtonsHiddenUnsupported.current
    ) {
      // Measures how many sessions the fail-closed hide removes, and which param or flow type
      // caused it, so the cost of waiting for SNOW-4313138 is visible per reason.
      logEvent({
        event: SignupEventName.OIDC_BUTTONS_HIDDEN_UNSUPPORTED,
        type: SignupEventType.UI_RESPONSE_SUCCESS,
        data: {
          formId,
          googleExperimentGroup,
          microsoftExperimentGroup,
          // Closed set only: `param:<name>` for the blocking params and `flowType:<SignupFlowType>`,
          // never a raw query-string value, since event data goes to analytics.
          reasons: unsupportedReasons,
          reasonCount: unsupportedReasons.length,
        },
      });
      hasLoggedButtonsHiddenUnsupported.current = true;
    }
  }, [
    isGoogleEnabled,
    isMicrosoftEnabled,
    wouldShowOidcButtons,
    isMumbaiSsoHideApplicable,
    isSpecialLinkHideApplicable,
    isUnsupportedHideApplicable,
  ]);

  const handleOidcLogin = React.useCallback(
    async (provider: OidcProvider) => {
      if (pendingProvider !== null) return;
      if (!hasRequiredContext || !formData) {
        return;
      }

      setPendingProvider(provider);
      setError(null);

      const oidcExperimentGroup =
        provider === OidcProvider.GOOGLE ? googleExperimentGroup : microsoftExperimentGroup;

      logEvent({
        event: SignupEventName.OIDC_BUTTON_CLICKED,
        type: SignupEventType.UI_RESPONSE_SUCCESS,
        data: {
          formId,
          provider,
          oidcExperimentGroup,
        },
      });

      try {
        const result = await SignupApi.startOidcSignup({ provider, formId });
        const oidcContext: OidcSignupContext = {
          edition: formData.edition,
          cloud: formData.cloud,
          region: formData.region,
          country,
          signupFlowType: getSignupFlowTypeWithoutListener(),
          originalSearch: window.location.pathname + window.location.search,
          // Explicit allowlist for the fields below (never a spread of signupParams/
          // marketingParams) -- keeps eng/ampt/awsType/t/listing/abexperiment/k/firstname/
          // lastname/email out of the per-field OIDC context on purpose; those are either out of
          // scope or security-sensitive (see SocialLoginButtons module docs / SNOW-4306083
          // follow-up). originalSearch above is the one exception that can contain them: it is
          // the raw search, kept only so the callback error card can link back to the same signup
          // page. It is validated as a same-origin relative path on read, only ever used as that
          // link's href, never sent to the server, and never leaves this tab's sessionStorage.
          template: stringOrUndefined(signupParams.template),
          lab: stringOrUndefined(signupParams.lab),
          workspaces: stringOrUndefined(signupParams.workspaces),
          account: stringOrUndefined(signupParams.account),
          token: stringOrUndefined(signupParams.token),
          E6CCEE4CEB: stringOrUndefined(signupParams.E6CCEE4CEB),
          referrer: stringOrUndefined(signupParams.referrer),
          owner: stringOrUndefined(signupParams.owner),
          sdm: booleanOrUndefined(signupParams.sdm),
          utm_adgroup: stringOrUndefined(marketingParams.utm_adgroup),
          utm_source: stringOrUndefined(marketingParams.utm_source),
          utm_medium: stringOrUndefined(marketingParams.utm_medium),
          utm_campaign: stringOrUndefined(marketingParams.utm_campaign),
          utm_content: stringOrUndefined(marketingParams.utm_content),
          utm_term: stringOrUndefined(marketingParams.utm_term),
          utm_region: stringOrUndefined(marketingParams.utm_region),
          gclid: stringOrUndefined(marketingParams.gclid),
        };
        sessionStorage.setItem('oidcInFlightCtx', result.inFlightCtx);
        sessionStorage.setItem(OIDC_FORM_ID_STORAGE_KEY, formId);
        sessionStorage.setItem('oidcProvider', provider);
        sessionStorage.setItem('oidcStartTime', String(Date.now()));
        sessionStorage.setItem(OIDC_SIGNUP_CONTEXT_KEY, JSON.stringify(oidcContext));

        logEvent({
          event: SignupEventName.OIDC_REDIRECT_STARTED,
          type: SignupEventType.UI_RESPONSE_SUCCESS,
          data: {
            formId,
            provider,
            oidcExperimentGroup,
          },
        });

        window.location.href = result.redirectUrl;
      } catch (err) {
        logEvent({
          event: SignupEventName.OIDC_START_API_ERROR,
          type: SignupEventType.UI_RESPONSE_ERROR,
          data: {
            formId,
            provider,
            oidcExperimentGroup,
            error: err instanceof Error ? err.message : String(err),
            ...(err instanceof OidcStartError ? { errorCode: err.code } : {}),
          },
          interaction: false,
        });
        setError(
          err instanceof OidcStartError && OIDC_START_UNSUPPORTED_CODES.includes(err.code)
            ? "Signing up with Google or Microsoft isn't available for this link. Please use your email below."
            : `Couldn't start sign-in with ${provider === OidcProvider.GOOGLE ? 'Google' : 'Microsoft'}. Please try again.`,
        );
        setPendingProvider(null);
      }
    },
    [
      formId,
      formData,
      country,
      hasRequiredContext,
      logEvent,
      googleExperimentGroup,
      microsoftExperimentGroup,
      pendingProvider,
      signupParams,
      marketingParams,
    ],
  );

  if (!isGoogleEnabled && !isMicrosoftEnabled) {
    return null;
  }

  const disabled = pendingProvider !== null;

  return (
    <>
      <div className="social-login-buttons">
        {isGoogleEnabled && (
          <button
            className="social-login-button"
            onClick={() => handleOidcLogin(OidcProvider.GOOGLE)}
            type="button"
            disabled={disabled || !hasRequiredContext}
          >
            <GoogleIcon />
            <span className="social-login-button__label">
              {pendingProvider === OidcProvider.GOOGLE ? (
                <>
                  Connecting to <strong>Google</strong>…
                </>
              ) : (
                <>
                  Sign up with <strong>Google</strong>
                </>
              )}
            </span>
          </button>
        )}
        {isMicrosoftEnabled && (
          <button
            className="social-login-button"
            onClick={() => handleOidcLogin(OidcProvider.MICROSOFT)}
            type="button"
            disabled={disabled || !hasRequiredContext}
          >
            <MicrosoftIcon />
            <span className="social-login-button__label">
              {pendingProvider === OidcProvider.MICROSOFT ? (
                <>
                  Connecting to <strong>Microsoft</strong>…
                </>
              ) : (
                <>
                  Sign up with <strong>Microsoft</strong>
                </>
              )}
            </span>
          </button>
        )}
        {error && (
          <p className="social-login-buttons__error" role="alert">
            {error}
          </p>
        )}
      </div>
      <div className="social-login-divider">
        <div className="social-login-divider__line" />
        <span className="social-login-divider__text">or continue with email</span>
        <div className="social-login-divider__line" />
      </div>
    </>
  );
};
